import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { toast } from "sonner";
import { 
  BookOpen, 
  MessageSquare, 
  Film, 
  Hash, 
  Compass,
  Copy,
  Check,
  Instagram,
  Facebook,
  Linkedin,
  ChevronRight,
  Download,
  Loader2,
  Search,
  Sparkles
} from "lucide-react";
import { API } from "../../apiConfig";


const ICON_MAP = {
  "message-square": MessageSquare,
  "film": Film,
  "book-open": BookOpen,
  "hash": Hash,
  "compass": Compass,
};

const PLATFORM_ICONS = {
  "Instagram": Instagram,
  "Facebook": Facebook,
  "LinkedIn": Linkedin,
  "Twitter/X": MessageSquare,
  "TikTok": Film,
};

const ResourceLibrary = ({ token }) => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedItem, setCopiedItem] = useState(null);
  const [selectedResource, setSelectedResource] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const res = await axios.get(`${API}/missions/resources`, { headers });
      setResources(res.data.resources || []);
    } catch (error) {
      console.error("Error fetching resources:", error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, itemId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(itemId);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopiedItem(null), 2000);
    } catch (err) {
      toast.error("Failed to copy");
    }
  };

  const copyAllHashtags = (tags) => {
    const text = tags.join(" ");
    copyToClipboard(text, "hashtags");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 rounded-xl bg-purple-500 text-white">
              <BookOpen className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">Resource Library</h3>
              <p className="text-sm text-muted-foreground">
                Everything you need to promote effectively
              </p>
            </div>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search resources..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white"
            />
          </div>
        </CardContent>
      </Card>

      {/* Resource Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {resources
          .filter(r => r.title.toLowerCase().includes(searchTerm.toLowerCase()))
          .map((resource) => {
            const IconComponent = ICON_MAP[resource.icon] || BookOpen;
            return (
              <Card 
                key={resource.id} 
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => setSelectedResource(resource)}
              >
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-purple-100 text-purple-600">
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold mb-1">{resource.title}</h4>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {resource.description}
                      </p>
                      <div className="flex items-center mt-3 text-purple-600 text-sm font-medium">
                        View {resource.items?.length || 0} items
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
      </div>

      {/* Resource Detail Dialog */}
      <Dialog open={!!selectedResource} onOpenChange={() => setSelectedResource(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedResource && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {(() => {
                    const IconComponent = ICON_MAP[selectedResource.icon] || BookOpen;
                    return <IconComponent className="w-5 h-5 text-purple-600" />;
                  })()}
                  {selectedResource.title}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                {/* Captions Resource */}
                {selectedResource.id === "captions" && (
                  <Accordion type="single" collapsible className="w-full">
                    {selectedResource.items.map((item, idx) => {
                      const PlatformIcon = PLATFORM_ICONS[item.platform] || MessageSquare;
                      return (
                        <AccordionItem key={idx} value={`caption-${idx}`}>
                          <AccordionTrigger className="hover:no-underline">
                            <div className="flex items-center gap-2">
                              <PlatformIcon className="w-4 h-4" />
                              {item.platform}
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="bg-muted p-4 rounded-lg">
                              <p className="whitespace-pre-wrap text-sm mb-4">
                                {item.caption}
                              </p>
                              <Button 
                                size="sm"
                                onClick={() => copyToClipboard(item.caption, `caption-${idx}`)}
                                variant={copiedItem === `caption-${idx}` ? "outline" : "default"}
                              >
                                {copiedItem === `caption-${idx}` ? (
                                  <><Check className="w-4 h-4 mr-2" /> Copied!</>
                                ) : (
                                  <><Copy className="w-4 h-4 mr-2" /> Copy Caption</>
                                )}
                              </Button>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                )}

                {/* Story Templates */}
                {selectedResource.id === "story_templates" && (
                  <div className="space-y-4">
                    {selectedResource.items.map((item, idx) => (
                      <Card key={idx}>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base flex items-center gap-2">
                            <Film className="w-4 h-4 text-purple-500" />
                            {item.title}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="bg-muted p-4 rounded-lg">
                            <p className="whitespace-pre-wrap text-sm mb-4">
                              {item.content}
                            </p>
                            <Button 
                              size="sm"
                              onClick={() => copyToClipboard(item.content, `story-${idx}`)}
                              variant={copiedItem === `story-${idx}` ? "outline" : "default"}
                            >
                              {copiedItem === `story-${idx}` ? (
                                <><Check className="w-4 h-4 mr-2" /> Copied!</>
                              ) : (
                                <><Copy className="w-4 h-4 mr-2" /> Copy Template</>
                              )}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}

                {/* Talking Points */}
                {selectedResource.id === "talking_points" && (
                  <Accordion type="single" collapsible className="w-full">
                    {selectedResource.items.map((item, idx) => (
                      <AccordionItem key={idx} value={`point-${idx}`}>
                        <AccordionTrigger className="hover:no-underline">
                          <div className="flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-amber-500" />
                            {item.title}
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <ul className="space-y-2">
                            {item.points.map((point, pidx) => (
                              <li key={pidx} className="flex items-start gap-2 text-sm">
                                <div className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2" />
                                {point}
                              </li>
                            ))}
                          </ul>
                          <Button 
                            size="sm"
                            className="mt-4"
                            onClick={() => copyToClipboard(item.points.join("\n• "), `point-${idx}`)}
                            variant={copiedItem === `point-${idx}` ? "outline" : "default"}
                          >
                            {copiedItem === `point-${idx}` ? (
                              <><Check className="w-4 h-4 mr-2" /> Copied!</>
                            ) : (
                              <><Copy className="w-4 h-4 mr-2" /> Copy All Points</>
                            )}
                          </Button>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}

                {/* Hashtags */}
                {selectedResource.id === "hashtags" && (
                  <div className="space-y-6">
                    {selectedResource.items.map((item, idx) => (
                      <div key={idx}>
                        <h4 className="font-medium mb-3 flex items-center gap-2">
                          <Hash className="w-4 h-4 text-blue-500" />
                          {item.category}
                        </h4>
                        <div className="flex flex-wrap gap-2 mb-3">
                          {item.tags.map((tag, tidx) => (
                            <Badge 
                              key={tidx} 
                              variant="secondary"
                              className="cursor-pointer hover:bg-blue-100"
                              onClick={() => copyToClipboard(tag, `tag-${idx}-${tidx}`)}
                            >
                              {tag}
                              {copiedItem === `tag-${idx}-${tidx}` && (
                                <Check className="w-3 h-3 ml-1" />
                              )}
                            </Badge>
                          ))}
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => copyAllHashtags(item.tags)}
                        >
                          <Copy className="w-4 h-4 mr-2" /> Copy All
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Platform Tips */}
                {selectedResource.id === "platform_tips" && (
                  <Accordion type="single" collapsible className="w-full">
                    {selectedResource.items.map((item, idx) => {
                      const PlatformIcon = PLATFORM_ICONS[item.platform] || Compass;
                      return (
                        <AccordionItem key={idx} value={`platform-${idx}`}>
                          <AccordionTrigger className="hover:no-underline">
                            <div className="flex items-center gap-2">
                              <PlatformIcon className="w-4 h-4" />
                              {item.platform}
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            <ul className="space-y-2">
                              {item.tips.map((tip, tidx) => (
                                <li key={tidx} className="flex items-start gap-2 text-sm">
                                  <div className="w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0 text-xs font-medium">
                                    {tidx + 1}
                                  </div>
                                  {tip}
                                </li>
                              ))}
                            </ul>
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResourceLibrary;
